import { useQuery } from "@tanstack/react-query";
import { ChevronRightIcon } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { Dot } from "@/components/app/record";
import { Card } from "@/components/ui/card";
import { accountNextQuery, tournamentNowQuery } from "@/lib/queries";
import { tournamentHref } from "@/lib/tournament";
import { cn } from "@/lib/utils";
import {
  fromSquad,
  type NowSide,
  opponentOf,
  pickSquad,
  type SquadNow,
  squadState,
  type TournamentNow,
} from "../../../shared/now";
import { parseLocal } from "../../../shared/time";
import { phaseOf, type TournamentSummary, todayLocal } from "../../../shared/tournament";
import { useT } from "../../i18n";
import { clockOf, weekdayShort } from "../../i18n/format";
import { periodShort } from "../../i18n/labels";

/**
 * The front page's first card (2026-09-16, the owner's pick from three
 * mockups): the answer to "when do I play?" before the list of tournaments.
 *
 * Signed in with a squad at an event being played: that squad's game on the
 * field with its score, or the result of the hour before, or the next
 * kickoff with a countdown - and a footer to the squad's schedule. Everyone
 * else, while an event is on: what the fields are playing right now, else
 * the next kickoff, else the last result - and a footer to the board's
 * schedule. Nothing to say: no card, and the page is what it was.
 *
 * Reads: `/api/account/next` for the person (after the lazy session check
 * the account door also makes, so the main chunk stays without the
 * capability tables), or `/api/t/<id>/now` for the stranger; both every
 * twenty seconds while the card shows, the way the overlay's ticker polls.
 * The clock ticks locally every thirty seconds for the countdown.
 */
export function NextMatchCard({
  tournaments,
  featuredSlug,
}: {
  tournaments: TournamentSummary[];
  /** The event this device opened last: the one to report on when several are on. */
  featuredSlug?: string | undefined;
}) {
  const signedIn = useSignedIn();
  const now = useNow(30_000);
  const today = todayLocal(new Date(now));

  const mine = useQuery({
    ...accountNextQuery(),
    enabled: signedIn === true,
    refetchInterval: 20_000,
  });
  const squad = signedIn === true && mine.data !== undefined ? pickSquad(mine.data) : null;
  const playing = tournaments
    .filter((row) => row.status !== "registration" && phaseOf(row, new Date(now)) === "now")
    .sort((a, b) => a.startDate.localeCompare(b.startDate));
  const current = playing.find((row) => row.slug === featuredSlug) ?? playing[0];
  // A stranger, or a person whose squads have nothing on: the event's card.
  const stranger = signedIn === false || (signedIn === true && mine.isSuccess && squad === null);
  const event = useQuery({
    ...tournamentNowQuery(current?.id ?? ""),
    enabled: current !== undefined && stranger,
    refetchInterval: 20_000,
  });
  if (squad !== null) return <SquadCard squad={squad} now={now} today={today} />;
  const data = event.data;
  if (
    current === undefined ||
    !stranger ||
    data === undefined ||
    (data.live.length === 0 && nextGamesOf(data).length === 0 && data.recent === null)
  )
    return null;
  return <EventCard data={data} now={now} today={today} />;
}

/** The games of the next kickoff. `nextGames` is the list; a Worker from
 *  before it existed sends `next` alone, for the minutes a deploy takes. */
function nextGamesOf(data: TournamentNow): TournamentNow["nextGames"] {
  const list: TournamentNow["nextGames"] | undefined = data.nextGames;
  return list ?? (data.next !== null ? [data.next] : []);
}

function useSignedIn(): boolean | null {
  const [signedIn, setSignedIn] = useState<boolean | null>(null);
  useEffect(() => {
    let alive = true;
    void import("@/lib/session")
      .then(async ({ ensureSession, accountIdentity }) => {
        const state = await ensureSession();
        if (alive) setSignedIn(state.status === "active" && accountIdentity(state.info) !== null);
      })
      .catch(() => {
        if (alive) setSignedIn(false);
      });
    return () => {
      alive = false;
    };
  }, []);
  return signedIn;
}

function useNow(everyMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), everyMs);
    return () => clearInterval(id);
  }, [everyMs]);
  return now;
}

function timeOf(startAt: string): string {
  const d = parseLocal(startAt);
  return d === null ? startAt : clockOf(d);
}

function nextDay(today: string): string {
  const d = parseLocal(`${today}T12:00`);
  if (d === null) return today;
  d.setDate(d.getDate() + 1);
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** «сьогодні» / «завтра» / «сб», and «за 40 хв» / «за 3 год 10 хв» /
 *  the clock for another day. */
function useWhen() {
  const t = useT();
  return (game: { startAt: string; epoch: number }, now: number, today: string) => {
    const day = game.startAt.slice(0, 10);
    const d = parseLocal(game.startAt);
    const dayLabel =
      day === today
        ? t("front.next.today")
        : day === nextDay(today)
          ? t("front.next.tomorrow")
          : d === null
            ? day
            : weekdayShort(d);
    const mins = Math.round((game.epoch - now) / 60_000);
    const countdown =
      mins <= 0
        ? t("front.next.startingNow")
        : mins < 60
          ? t("front.next.inMinutes", { n: mins })
          : day === today
            ? t("front.next.inHours", { h: Math.floor(mins / 60), m: mins % 60 })
            : timeOf(game.startAt);
    return { day: dayLabel, countdown };
  };
}

const OUTCOME = {
  won: "front.next.won",
  lost: "front.next.lost",
  drawn: "front.next.drawn",
} as const;

function sideName(side: NowSide | null, fallback: string): string {
  return side?.name ?? fallback;
}

/**
 * The pane: a head that opens the thing itself, an optional list of games,
 * the footer to the schedule.
 *
 * ONE RHYTHM (2026-09-19, the owner: "spacing is weird"). Every section is a
 * cell at `px-4 py-3`, every rule is the card's own `divide-y` and therefore
 * runs edge to edge, and inside a cell the only steps are 8px under the
 * eyebrow and 4px under a title. Before this the card mixed four insets, drew
 * inset rules inside the body against a full-bleed one under the footer, and
 * carried `last:pb-0` on an `<a>` that is its `<li>`'s only child - so the
 * variant fired on EVERY row and each one was padded at the top only.
 *
 * NO JERSEY STRIPE. `.stripe` marks one member of a LIST against its siblings
 * (GLASS.md §4); this card stands alone above the tournament list, which wears
 * the same pink 12px below it for the event being played - two different kinds
 * of thing carrying one mark on one screen is what stops the mark meaning
 * anything. Removing it also squares the insets: the 3px border made the left
 * 19px against the right's 16.
 */
function Shell({
  eyebrow,
  day,
  bodyHref,
  list,
  footerHref,
  footerLabel,
  children,
}: {
  eyebrow: ReactNode;
  /** The day, kept out of the truncating half: it is the one fact on that
   *  line the rows below do not repeat, and «НАСТУПНІ ІГРИ · <event> ·
   *  СЬОГОДНІ» is longer than a phone at `.eyebrow`'s 0.3em tracking. */
  day?: string | undefined;
  bodyHref?: string | undefined;
  /** Games, as a sibling of the head: a rule between them can only be
   *  full-bleed if the list is not nested inside the head's padding. */
  list?: ReactNode;
  footerHref: string;
  footerLabel: string;
  /** What the head says under its eyebrow. A card whose games are all in the
   *  `list` has none, and then the head is the eyebrow alone. */
  children?: ReactNode;
}) {
  const head = (
    <>
      <div className="eyebrow flex items-center gap-2 text-muted-foreground">
        {/* The name ellipses; the day never shrinks. `truncate` has to sit on
            the element that HOLDS THE TEXT: on the flex row it clipped, since
            a bare string is an anonymous flex item whose minimum size is the
            whole string and the row has no inline content of its own to
            ellipsise. A node eyebrow (the live branch's dot and name) brings
            its own truncating span. */}
        <span className="flex min-w-0 items-center gap-2">
          {typeof eyebrow === "string" ? (
            <span className="min-w-0 truncate">{eyebrow}</span>
          ) : (
            eyebrow
          )}
        </span>
        {day !== undefined && <span className="shrink-0">· {day}</span>}
      </div>
      {children !== undefined && children !== null && <div className="mt-2">{children}</div>}
    </>
  );
  return (
    <Card className="gap-0 divide-y p-0">
      {bodyHref !== undefined ? (
        <a href={bodyHref} className="block px-4 py-3 transition-colors hover:bg-accent">
          {head}
        </a>
      ) : (
        <div className="px-4 py-3">{head}</div>
      )}
      {list}
      <a
        href={footerHref}
        className="flex items-center justify-between gap-3 px-4 py-3 text-sm font-medium text-brand-ink transition-colors hover:bg-accent dark:text-brand"
      >
        {footerLabel}
        <ChevronRightIcon aria-hidden="true" className="size-4 shrink-0" />
      </a>
    </Card>
  );
}

/** A game in the card's list: its own cell, and its own tap target. */
function GameRow({ href, children }: { href: string; children: ReactNode }) {
  return (
    <li>
      <a href={href} className="block px-4 py-3 transition-colors hover:bg-accent">
        {children}
      </a>
    </li>
  );
}

function Matchup({ a, b }: { a: string; b: string }) {
  return (
    <p className="nxt text-xl leading-tight text-pretty">
      {a} – {b}
    </p>
  );
}

/** The kickoff's line: when and where on the left, how long until it on the
 *  right. Its own line under the matchup - beside it, a wrapped team name ran
 *  straight into «ЗА 40 ХВ» at 375. */
function Countdown({ when, countdown }: { when: string; countdown: string }) {
  return (
    <div className="mt-1 flex items-baseline justify-between gap-3">
      <p className="tabular text-sm text-muted-foreground">{when}</p>
      <span className="nxt shrink-0 text-lg leading-none">{countdown}</span>
    </div>
  );
}

function Score({
  us,
  them,
  tag,
  tone,
}: {
  us: number;
  them: number;
  tag: string;
  tone?: "go" | undefined;
}) {
  return (
    <div className="mt-1 flex items-baseline gap-3">
      <span className="nxt tabular text-3xl leading-none">
        {us} : {them}
      </span>
      <span
        className={cn(
          "text-xs font-semibold tracking-wide uppercase",
          tone === "go" ? "text-go-ink" : "text-muted-foreground",
        )}
      >
        {tag}
      </span>
    </div>
  );
}

function SquadCard({ squad, now, today }: { squad: SquadNow; now: number; today: string }) {
  const t = useT();
  const when = useWhen();
  const hub = tournamentHref(squad.eventSlug, `/team/${squad.teamId}`);
  const gameHref = (gameId: string | null): string =>
    gameId === null ? hub : tournamentHref(squad.eventSlug, `/games/${gameId}`);
  const tbd = t("front.next.tbd");
  const state = squadState(squad);

  if (state === "live" && squad.live !== null) {
    const g = squad.live;
    const s = fromSquad(g, squad.teamId);
    return (
      <Shell
        eyebrow={
          <Dot
            tone="ok"
            pulse
            label={t("front.next.onField", { field: g.field })}
            className="text-go-ink"
          />
        }
        bodyHref={gameHref(g.gameId)}
        footerHref={hub}
        footerLabel={t("front.next.teamSchedule")}
      >
        <Matchup a={squad.teamName} b={sideName(opponentOf(g, squad.teamId), tbd)} />
        <Score us={s.us} them={s.them} tag={periodShort(g.period)} />
      </Shell>
    );
  }
  if (state === "recent" && squad.recent !== null) {
    const g = squad.recent;
    const s = fromSquad(g, squad.teamId);
    const next = squad.next;
    return (
      <Shell
        eyebrow={t("front.next.played", { time: timeOf(g.startAt) })}
        bodyHref={gameHref(g.gameId)}
        footerHref={hub}
        footerLabel={t("front.next.teamSchedule")}
        list={
          next === null ? undefined : (
            // A cell of its own, so its rule is the card's full-bleed one
            // rather than a fourth geometry inside the head's padding.
            <p className="px-4 py-3 text-sm text-muted-foreground text-pretty">
              {t("front.next.then", {
                opponent: sideName(opponentOf(next, squad.teamId), tbd),
                time: timeOf(next.startAt),
                field: next.field,
              })}
            </p>
          )
        }
      >
        <Matchup a={squad.teamName} b={sideName(opponentOf(g, squad.teamId), tbd)} />
        <Score
          us={s.us}
          them={s.them}
          tag={t(OUTCOME[s.outcome])}
          tone={s.outcome === "won" ? "go" : undefined}
        />
      </Shell>
    );
  }
  if (state === "next" && squad.next !== null) {
    const g = squad.next;
    const { day, countdown } = when(g, now, today);
    return (
      <Shell
        eyebrow={t("front.next.eyebrow")}
        day={day}
        bodyHref={hub}
        footerHref={hub}
        footerLabel={t("front.next.teamSchedule")}
      >
        <Matchup a={squad.teamName} b={sideName(opponentOf(g, squad.teamId), tbd)} />
        <Countdown when={`${timeOf(g.startAt)} · ${g.field}`} countdown={countdown} />
      </Shell>
    );
  }
  return null;
}

function EventCard({ data, now, today }: { data: TournamentNow; now: number; today: string }) {
  const t = useT();
  const when = useWhen();
  const schedule = `${tournamentHref(data.slug)}#next`;
  const gameHref = (gameId: string | null): string =>
    gameId === null ? tournamentHref(data.slug) : tournamentHref(data.slug, `/games/${gameId}`);

  if (data.live.length > 0) {
    return (
      <Shell
        eyebrow={
          <>
            <Dot
              tone="ok"
              pulse
              label={t("front.next.onFields")}
              className="shrink-0 text-go-ink"
            />
            <span className="min-w-0 truncate">· {data.name}</span>
          </>
        }
        footerHref={schedule}
        footerLabel={t("front.next.schedule")}
        list={
          /* A game a row: the matchup and the score on one line, the field
             and the period under them. Three columns beside a score crushed
             «NOVA – VW» to «NOVA – …» at 375. */
          <ul className="divide-y">
            {data.live.map((g) => (
              <GameRow key={g.fieldId} href={gameHref(g.gameId)}>
                <span className="flex items-center gap-3">
                  <span className="nxt min-w-0 flex-1 text-base leading-tight text-pretty">
                    {g.a.short} – {g.b.short}
                  </span>
                  <span className="nxt tabular shrink-0 text-xl leading-none">
                    {g.scoreA} : {g.scoreB}
                  </span>
                </span>
                <span className="mt-1 block text-xs text-muted-foreground">
                  {g.field} ·{" "}
                  <span className="tracking-wide uppercase">{periodShort(g.period)}</span>
                </span>
              </GameRow>
            ))}
          </ul>
        }
      />
    );
  }
  const upcoming = nextGamesOf(data);
  const first = upcoming[0];
  if (first !== undefined && upcoming.length === 1) {
    const g = first;
    const { day, countdown } = when(g, now, today);
    return (
      <Shell
        eyebrow={t("front.next.nextGame", { name: data.name })}
        day={day}
        bodyHref={gameHref(g.gameId)}
        footerHref={schedule}
        footerLabel={t("front.next.schedule")}
      >
        <Matchup a={sideName(g.a, "?")} b={sideName(g.b, "?")} />
        <Countdown when={`${timeOf(g.startAt)} · ${g.field}`} countdown={countdown} />
      </Shell>
    );
  }
  if (first !== undefined) {
    // A ROUND, not a game (owner, 2026-09-19): two pitches kick off together
    // and the card named one of them. One kickoff line for them all, then a
    // game a row with its pitch, the live list's shape, each row opening its
    // own game.
    const { day, countdown } = when(first, now, today);
    return (
      <Shell
        eyebrow={t("front.next.nextGames", { name: data.name })}
        day={day}
        footerHref={schedule}
        footerLabel={t("front.next.schedule")}
        list={
          <ul className="divide-y">
            {upcoming.map((g) => (
              <GameRow key={g.gameId} href={gameHref(g.gameId)}>
                <span className="nxt block text-base leading-tight text-pretty">
                  {sideName(g.a, "?")} – {sideName(g.b, "?")}
                </span>
                <span className="mt-1 block text-xs text-muted-foreground">{g.field}</span>
              </GameRow>
            ))}
          </ul>
        }
      >
        {/* The same line the single-game card draws, so one kickoff is not
            printed two ways: the time quiet on the left, how long until it in
            the display face on the right. The field is on each row. */}
        <Countdown when={timeOf(first.startAt)} countdown={countdown} />
      </Shell>
    );
  }
  if (data.recent !== null) {
    const g = data.recent;
    return (
      <Shell
        eyebrow={t("front.next.lastResult", { name: data.name })}
        bodyHref={gameHref(g.gameId)}
        footerHref={schedule}
        footerLabel={t("front.next.schedule")}
      >
        <Matchup a={sideName(g.a, "?")} b={sideName(g.b, "?")} />
        <Score us={g.scoreA} them={g.scoreB} tag={`${timeOf(g.startAt)} · ${g.field}`} />
      </Shell>
    );
  }
  return null;
}
